import setPlainTextEditorSelection from '../actions/setPlainTextEditorSelection';
import updateContent from '../actions/updateContent';
import EditorViewState from '../store/schema/EditorViewState';
import { addContentOperator, removeContentOperator } from '../utils/ContentOperator';
import * as React from 'react';

export interface PlainTextEditorProps {
    viewState: EditorViewState;
    plainTextPlaceholder: string;
    onFocus?: () => void;
    classNames?: string;
    isEditorDisabled?: boolean;
}

export interface PlainTextEditorHandle {
    focus(): void;
}

export default React.forwardRef(function PlainTextEditor(
    {
        classNames,
        plainTextPlaceholder,
        onFocus,
        isEditorDisabled,
        viewState,
    }: PlainTextEditorProps,
    ref: React.Ref<PlainTextEditorHandle>
) {
    const textArea = React.useRef<HTMLTextAreaElement>();

    React.useEffect(() => {
        const { content, selectionStart, selectionEnd } = viewState;
        addContentOperator(
            viewState,
            null /* htmlEditor */,
            textArea.current /* plainTextEditor */
        );

        // Browsers like Edge will move the selection start and end to
        // the end of the content when the value of the input is updated
        // Save these values so we can maintain the initial selection
        const initialStart = textArea.current.selectionStart;
        const initialEnd = textArea.current.selectionEnd;
        textArea.current.value = content;
        textArea.current.selectionStart = selectionStart != null ? selectionStart : initialStart;
        textArea.current.selectionEnd = selectionEnd != null ? selectionEnd : initialEnd;

        return () => {
            removeContentOperator(viewState, null /*htmlEditor*/, textArea.current);
        };
    }, []);

    React.useImperativeHandle(
        ref,
        () => ({
            focus() {
                textArea.current.focus();

                const { selectionStart, selectionEnd } = viewState;
                if (selectionStart != null && selectionEnd != null) {
                    textArea.current.selectionStart = selectionStart;
                    textArea.current.selectionEnd = selectionEnd;

                    // reset values in view state
                    setPlainTextEditorSelection(
                        viewState,
                        null /* selectionStart */,
                        null /* selectionEnd */
                    );
                }
            },
        }),
        [viewState, viewState?.selectionStart, viewState?.selectionEnd]
    );

    const onChange = React.useCallback(() => {
        updateContent(viewState, textArea.current.value);
    }, [viewState]);

    return (
        <textarea
            ref={textArea}
            className={classNames}
            placeholder={plainTextPlaceholder || ''}
            onFocus={onFocus}
            onChange={onChange}
            readOnly={isEditorDisabled}
        />
    );
});
